import type { Address } from '@my/directory-api';
import type { CargoDefinition, Order, OrderTask, ServiceTag, TaskTypeValue } from '@my/tms-api';
import { Check, Clock } from 'lucide-react';
import { adaptTaskType } from '../lib/adapters';
import { cargoInfoRows, isUnitRow } from '../lib/cargoInfo';
import { formatTaskDate } from '../lib/dataCells';
import { TASK_TYPE_CONFIG, taskTypeLabel } from '../lib/taskTypeConfig';

/*
 * The cells the Orders screen draws an order with — cargo, cargo details,
 * start / end point, route — shared so every list of orders (the Orders
 * screen, the previous-orders list on New order) reads the same.
 */

/**
 * A task's planned date over its time ("Thu 24 Sept" / "Any"), as the
 * Orders screen shows a start or end point. Shared so every list of orders
 * dates them the same way.
 */
export function TaskDateTime({ task }: { task: OrderTask }) {
  return (
    <div className="space-y-0.5">
      <div className="whitespace-nowrap text-[11px] font-medium text-slate-600">
        {formatTaskDate(task.plannedDate ?? undefined)}
      </div>
      <div className="flex items-center gap-1 text-[10px] text-slate-400">
        <Clock className="h-3 w-3" aria-hidden="true" />
        {formatPlannedTime(task)}
      </div>
    </div>
  );
}

/**
 * Cargo cell. Headline is the cargo definition (with the variant appended
 * for IsoTank / Container), under a wrapping chip row of the order's service
 * tags. What is actually ON the job — the box's BIC code, the tank's ID, or the
 * lines of goods — lives in the Info cell next door: it belongs to one column
 * with one meaning, rather than competing with the definition for the same
 * truncating line.
 */
export function CargoCell({
  order,
  cargoDef,
  serviceTagsById,
}: {
  order: Order;
  cargoDef: CargoDefinition | undefined;
  serviceTagsById: Map<string, ServiceTag>;
}) {
  const variantName = order.cargoDefinitionVariantId
    ? (cargoDef?.variants.find((v) => v.id === order.cargoDefinitionVariantId)?.name ?? null)
    : null;
  const headline = cargoDef
    ? variantName
      ? `${cargoDef.displayName} · ${variantName}`
      : cargoDef.displayName
    : '—';

  const tagNames = order.serviceTagIds
    .map((id) => serviceTagsById.get(id)?.name)
    .filter((n): n is string => !!n);

  return (
    <div className="min-w-[160px] max-w-[240px] space-y-1">
      <div className="flex items-baseline gap-1.5">
        <span className="shrink-0 whitespace-nowrap text-xs font-semibold text-slate-800">
          {headline}
        </span>
      </div>
      {tagNames.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          {tagNames.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Start- / end-point cell. Two side-by-side columns, each two rows deep,
 * matching the mock:
 *   • Col 1 — planned date (top) over planned time (bottom).
 *   • Col 2 — a type-coloured dot + site name (top) over the full address
 *     (bottom).
 * The dot is keyed off the task *type* (green = pickup / load / collect,
 * blue = delivery / unload, purple = booking, grey = everything else), so
 * an operator can read the leg's role at a glance. Renders a muted skeleton
 * while the order's tasks are still in flight, and a dash once we know it
 * has none.
 */
export function EndpointCell({
  task,
  address,
  pending,
}: {
  task: OrderTask | undefined;
  address: Address | undefined;
  pending: boolean;
}) {
  if (!task) {
    return pending ? (
      <span className="text-[11px] italic text-slate-300">…</span>
    ) : (
      <span className="text-[11px] italic text-slate-300">—</span>
    );
  }

  // Port / terminal Location records carry no Google `fullAddress`, so fall
  // back to the locality so the line never renders empty.
  const fullAddress = address?.fullAddress?.trim() || address?.city?.trim() || null;

  return (
    <div className="flex gap-3">
      {/* Col 1 — planned date over time */}
      <TaskDateTime task={task} />

      {/* Col 2 — type-coloured dot + site name over full address */}
      <div className="min-w-0 max-w-[200px]">
        <div className="flex items-center gap-1.5">
          <span
            className={`h-2 w-2 shrink-0 rounded-full ${taskDotColor(task.type)}`}
            aria-hidden="true"
          />
          <span className="truncate text-xs font-medium text-slate-800">
            {address?.name ?? 'Unknown site'}
          </span>
        </div>
        {fullAddress && (
          <div className="truncate pl-3.5 text-[11px] text-slate-500">{fullAddress}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Endpoint dot colour, keyed off the task type. Mirrors the three-tone rule
 * in `lib/taskTypeConfig` (green = load-side, blue = delivery-side, purple =
 * booking) but collapses every other type to grey, per the list-view spec.
 */
function taskDotColor(type: TaskTypeValue): string {
  switch (type) {
    case 'PickUp':
    case 'Load':
    case 'Collect':
      return 'bg-emerald-500';
    case 'DropOff':
    case 'Unload':
    case 'Deliver':
      return 'bg-blue-500';
    case 'BookingDeparture':
    case 'BookingArrival':
      return 'bg-purple-500';
    default:
      return 'bg-slate-400';
  }
}

/**
 * Horizontal route strip — one marker per task in execution order, joined
 * by connector lines. Booking halves get a ferry glyph; completed stops a
 * green tick; everything else a hollow dot. The code beneath each marker is
 * the address's short code (falling back to initials) prefixed by its ISO
 * country.
 */
export function RouteStrip({
  tasks,
  addressById,
  pending,
}: {
  tasks: OrderTask[];
  addressById: Map<string, Address>;
  pending: boolean;
}) {
  if (tasks.length === 0) {
    return pending ? (
      <span className="text-[11px] italic text-slate-300">…</span>
    ) : (
      <span className="text-[11px] italic text-slate-300">—</span>
    );
  }

  return (
    <div className="flex items-start">
      {tasks.map((task, i) => {
        const address = addressById.get(task.addressId);
        const prevCompleted = i > 0 && tasks[i - 1]?.state === 'Completed';
        return (
          <div key={task.id} className="flex items-start">
            {i > 0 && (
              <span
                className={`mt-2.5 h-px w-5 ${prevCompleted ? 'bg-green-400' : 'bg-slate-300'}`}
                aria-hidden="true"
              />
            )}
            <div className="flex w-14 flex-col items-center gap-1 text-center">
              <RouteMarker task={task} />
              <span className="text-[10px] leading-tight">
                <span className="text-slate-400">{address?.countryIsoCode ?? '??'} </span>
                <span className="font-medium text-slate-600">{stopCode(address)}</span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One-line start / next / end point, for a dense grid (Plan's Job Queue):
 * "Sat 17 Oct  ◷ AM  • Site name, address". The same pieces as
 * `EndpointCell` — date, time, the task-type dot, the site — side by side
 * instead of stacked, truncating at the end.
 */
export function EndpointInline({
  task,
  address,
  pending,
}: {
  task: OrderTask | undefined;
  address: Address | undefined;
  pending: boolean;
}) {
  if (!task) {
    return <span className="text-[11px] italic text-slate-300">{pending ? '…' : '—'}</span>;
  }
  const fullAddress = address?.fullAddress?.trim() || address?.city?.trim() || null;
  return (
    <div
      className="flex min-w-0 items-center gap-2 whitespace-nowrap"
      title={[address?.name, fullAddress].filter(Boolean).join(', ')}
    >
      <span className="text-[11px] font-medium text-slate-600">
        {formatTaskDate(task.plannedDate ?? undefined)}
      </span>
      <span className="flex items-center gap-1 text-[10px] text-slate-400">
        <Clock className="h-3 w-3" aria-hidden="true" />
        {formatPlannedTime(task)}
      </span>
      <span className={`h-2 w-2 shrink-0 rounded-full ${taskDotColor(task.type)}`} aria-hidden="true" />
      <span className="min-w-0 truncate text-xs">
        <span className="font-medium text-slate-800">{address?.name ?? 'Unknown site'}</span>
        {fullAddress && <span className="text-slate-500">, {fullAddress}</span>}
      </span>
    </div>
  );
}

/**
 * One-line route strip, for a dense grid: each stop's marker with its country
 * and code beside it rather than beneath, joined by short connectors. Same
 * markers, codes and "done" colouring as `RouteStrip`.
 */
export function RouteStripInline({
  tasks,
  addressById,
  pending,
}: {
  tasks: OrderTask[];
  addressById: Map<string, Address>;
  pending: boolean;
}) {
  if (tasks.length === 0) {
    return <span className="text-[11px] italic text-slate-300">{pending ? '…' : '—'}</span>;
  }
  return (
    <div className="flex items-center whitespace-nowrap">
      {tasks.map((task, i) => {
        const address = addressById.get(task.addressId);
        const prevCompleted = i > 0 && tasks[i - 1]?.state === 'Completed';
        return (
          <div key={task.id} className="flex items-center">
            {i > 0 && (
              <span
                className={`mx-1.5 h-px w-3 ${prevCompleted ? 'bg-green-400' : 'bg-slate-300'}`}
                aria-hidden="true"
              />
            )}
            <span className="flex items-center gap-1" title={address?.name}>
              <RouteMarker task={task} />
              <span className="text-[10px] leading-tight">
                <span className="text-slate-400">{address?.countryIsoCode ?? '??'} </span>
                <span className="font-medium text-slate-600">{stopCode(address)}</span>
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Route-strip marker. Uses the exact same icon + colour tone as the Order
 * Details Tasks panel (`lib/taskTypeConfig`), routed through `adaptTaskType`
 * so the BE's ~24 specialty types collapse to the same handful of buckets:
 * box = pickup / collect, truck = drop-off / deliver, droplet = load /
 * unload, ship = booking halves, and the process-event glyphs (clock / file
 * / search) for everything else.
 *
 * A completed task keeps its type icon (so the leg's role is still legible)
 * and gains a small green check badge in the corner — that's how "done"
 * reads on the strip.
 */
function RouteMarker({ task }: { task: OrderTask }) {
  const cfg = TASK_TYPE_CONFIG[adaptTaskType(task.type)];
  const Icon = cfg.icon;
  const done = task.state === 'Completed';
  return (
    <span className="relative inline-flex">
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${cfg.tone}`}
        title={done ? `${taskTypeLabel(task.type)} · done` : taskTypeLabel(task.type)}
      >
        <Icon className="h-3 w-3" />
      </span>
      {done && (
        <span
          className="absolute -bottom-0.5 -right-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 ring-1 ring-white"
          aria-hidden="true"
        >
          <Check className="h-2 w-2 text-white" strokeWidth={3} />
        </span>
      )}
    </span>
  );
}

/**
 * Right-hand time label for an endpoint. Mirrors the TimeModePicker pills:
 * AM / PM bucket, an exact `HH:mm`, else "Any".
 */
function formatPlannedTime(task: OrderTask): string {
  if (task.timeMode === 'AM') return 'AM';
  if (task.timeMode === 'PM') return 'PM';
  if (task.plannedTime) return task.plannedTime.slice(0, 5);
  return 'Any';
}

/**
 * Short stop code for the route strip — the address's own `code`
 * (uppercased), falling back to the initials of the first words of its
 * name so the chip never renders empty.
 */
function stopCode(address: Address | undefined): string {
  const code = address?.code?.trim();
  if (code) return code.toUpperCase();
  const name = address?.name?.trim() ?? '';
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return initials || '—';
}

/**
 * Info cell — the identifying detail for this order's cargo, which is a
 * different fact per handling family:
 *
 * <ul>
 *   <li><b>Container</b> — the BIC code(s) of the box(es) on the job.</li>
 *   <li><b>Tank</b> (iso tank, bulk liquids, bulk materials) — the Tank ID(s).</li>
 *   <li><b>Everything else</b> — the goods, as "Qty x Description".</li>
 * </ul>
 *
 * <p>
 * Plus the target temperature, on the orders that HAVE one — which is the test
 * for "is this a temperature order": `temperatureC` is only populated when the
 * picked cargo definition carries the temperature property group, and the BE
 * requires it there. Reading the definition's groups instead would be the same
 * answer with an extra way to disagree, and would go blank on an order whose
 * definition was edited after the fact.
 * </p>
 *
 * <p>
 * The family comes from the cargo DEFINITION's type (`cargoFamilyFor`, shared
 * with the order screen) rather than from the cargo rows' own `kind`, because
 * the kind is derived from the family at creation — reading it back would be
 * the same answer with an extra chance to disagree.
 * </p>
 *
 * <p>
 * Identifiers and the temperature are captioned ("BIC", "Tank", "Temp"), the
 * goods are not: a bare "MSCU1234567" says nothing about which field it came
 * from, while "10 x Euro Pallet" is self-describing. Tasks are still loading
 * → nothing is claimed, since an empty dash on a container job reads as "no box
 * on this order".
 * </p>
 */
export function CargoInfoCell({
  order,
  cargoDef,
  tasks,
  pending,
  hideUnits = false,
}: {
  order: Order;
  cargoDef: CargoDefinition | undefined;
  tasks: OrderTask[];
  pending: boolean;
  /**
   * Leave out the unit rows (Tank / BIC). "Start from a previous order" does:
   * a copy never carries the unit, so its number is noise there.
   */
  hideUnits?: boolean;
}) {
  const all = cargoInfoRows(order, cargoDef, tasks, pending);
  const rows = hideUnits ? all.filter((row) => !isUnitRow(row)) : all;

  if (rows.length === 0) {
    return (
      <span className="text-[11px] italic text-slate-300">{pending ? '…' : '—'}</span>
    );
  }

  return (
    <div className="min-w-[130px] space-y-0.5">
      {rows.map((row) =>
        row.caption ? (
          <InfoLine
            key={`${row.caption}-${row.value}`}
            caption={row.caption}
            value={row.value}
            title={row.title}
          />
        ) : (
          <span
            key={`goods-${row.value}`}
            className="block max-w-[220px] truncate text-[11px] text-slate-600"
            title={row.value}
          >
            {row.value}
          </span>
        ),
      )}
    </div>
  );
}



/** Captioned one-liner in the Info cell — "BIC MSCU1234567", "Temp -18°C". */
function InfoLine({ caption, value, title }: { caption: string; value: string; title?: string }) {
  return (
    <div className="flex items-baseline gap-1.5 whitespace-nowrap" title={title ?? value}>
      <span className="text-[10px] uppercase tracking-wide text-slate-400">{caption}</span>
      {/* Product names run long ("SULPHURIC ACID with more than 51 % acid"). */}
      <span className="max-w-[200px] truncate text-xs font-semibold text-slate-800">{value}</span>
    </div>
  );
}
